import { useState, useEffect } from "react";
import Paper from "@mui/material/Paper";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import { styled } from "@mui/material/styles";
import { HashLoader } from "react-spinners";
import Chip from "@mui/material/Chip";
import { MdOutlineFileCopy } from "react-icons/md";

import { COLORS } from "../../utils/colors";
import { Venue } from "../EventManagement/Venue";
import { Overview } from "./Overview";
import { Discounts } from "./Discounts";
import { TicketCategories } from "./TicketCategories";
import { getEventById } from "../../api/events";
import { SmartContract } from "./SmartContract";
import FormQuestions from "./FormQuestions";

const StyledToggleButtonGroup = styled(ToggleButtonGroup)(({ theme }) => ({
  "& .MuiToggleButtonGroup-grouped": {
    margin: theme.spacing(0.5),
    border: 0,
    textTransform: "none",
    width: "fit-content",
    "&.Mui-disabled": {
      border: "1px solid ${COLORS.grey}",
    },
    "&:not(:first-of-type)": {
      borderRadius: theme.shape.borderRadius,
    },
    "&:first-of-type": {
      borderRadius: theme.shape.borderRadius,
    },
  },
}));

export const ManageEvent = ({ userData, handleSnackbar, supabase }) => {
  const url = new URL(window.location.href);
  const id = url.searchParams.get("id");
  const [event, setEvent] = useState();
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState("overview");

  const changeTab = (event, newTab) => {
    newTab !== null && setTab(newTab);
  };

  useEffect(async () => {
    try {
      setLoading(true);

      const { data, error } = await getEventById(
        id,
        userData.operators[0].api_key
      );

      setEvent(data[0]);

      if (error) {
        console.log(error);
        handleSnackbar(error.message, "error");
        return;
      }

      setLoading(false);
    } catch (error) {
      setLoading(false);
      console.log(error);
      handleSnackbar(error, "error");
    }
  }, []);

  const copyToClipboard = async (text) => {
    await navigator.clipboard.writeText(text);
  };

  return (
    <div className="manage-event-container">
      <p id="subheader" style={{ marginBottom: "10px" }}>
        Event Management
      </p>
      <h1 style={{ marginBottom: "0px" }}>{event?.name}</h1>
      <Chip
        sx={{
          width: "fit-content",
          background: COLORS.evenLighterGrey,
          border: `1px solid ${COLORS.lightGrey}`,
          margin: "5px 0 20px",
        }}
        label={`ID: ${event?._id?.slice(0, 3)}...${event?._id?.slice(21)}`}
        icon={<MdOutlineFileCopy size={16} />}
        onClick={() => copyToClipboard(event._id)}
      />
      <Paper
        elevation={0}
        sx={{
          display: "flex",
          border: (theme) => `1px solid ${theme.palette.divider}`,
          width: "fit-content",
          flexWrap: "wrap",
          justifyContent: "space-between",
        }}
      >
        <StyledToggleButtonGroup
          size="small"
          value={tab}
          exclusive
          onChange={changeTab}
        >
          <ToggleButton
            value="overview"
            selected={tab == "overview"}
            sx={{ color: COLORS.grey }}
          >
            <p
              style={{
                fontSize: "14px",
                margin: "0px 20px",
              }}
            >
              Overview
            </p>
          </ToggleButton>
          <ToggleButton
            value="tickets"
            selected={tab == "tickets"}
            sx={{ color: COLORS.grey }}
          >
            <p style={{ fontSize: "14px", margin: "0px 20px" }}>Tickets</p>
          </ToggleButton>
          <ToggleButton
            value="discounts"
            selected={tab == "discounts"}
            sx={{ color: COLORS.grey }}
          >
            <p style={{ fontSize: "14px", margin: "0px 20px" }}>Discounts</p>
          </ToggleButton>
          <ToggleButton
            value="form-questions"
            selected={tab == "form-questions"}
            sx={{ color: COLORS.grey }}
          >
            <p style={{ fontSize: "14px", margin: "0px 20px" }}>
              Form Questions
            </p>
          </ToggleButton>
          <ToggleButton
            value="venue"
            selected={tab == "venue"}
            sx={{ color: COLORS.grey }}
          >
            <p style={{ fontSize: "14px", margin: "0px 20px" }}>Venue</p>
          </ToggleButton>
          {event?.smart_contract_address && (
            <ToggleButton
              value="smartContract"
              selected={tab == "smartContract"}
            >
              <p style={{ fontSize: "14px", margin: "0px 20px" }}>
                Smart Contract
              </p>
            </ToggleButton>
          )}
        </StyledToggleButtonGroup>
      </Paper>
      {loading && (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "300px",
          }}
        >
          <HashLoader color={COLORS.mainAccent} />
        </div>
      )}
      {event && tab === "overview" && (
        <Overview
          event={event}
          setEvent={setEvent}
          handleSnackbar={handleSnackbar}
          loading={loading}
          supabase={supabase}
          userData={userData}
        />
      )}
      {!loading && event && tab === "tickets" && (
        <TicketCategories
          event={event}
          setEvent={setEvent}
          handleSnackbar={handleSnackbar}
          supabase={supabase}
          userData={userData}
        />
      )}
      {!loading && event && tab === "discounts" && (
        <Discounts
          event={event}
          setEvent={setEvent}
          handleSnackbar={handleSnackbar}
        />
      )}
      {!loading && event && tab === "form-questions" && (
        <FormQuestions
          event={event}
          handleSnackbar={handleSnackbar}
          setEvent={setEvent}
        />
      )}
      {!loading && event && tab === "venue" && (
        <Venue
          event={event}
          handleSnackbar={handleSnackbar}
          setEvent={setEvent}
        />
      )}
      {!loading && event && tab === "smartContract" && (
        <SmartContract event={event} />
      )}
    </div>
  );
};
